import React, { useEffect, useState, useCallback } from "react";
import { AlertTriangle, Download, Loader } from "lucide-react";
import {
    getVacationModeStatus, prepareVacationMode, cancelVacationMode,
    activateVacationModePage, restoreVacationModePage, skipFailedVacationMode,
} from "../../services/vacationModeService";

const GaveUpList = ({ gaveUp, onSkip }) => (
    <div className="mb-3">
        <div className="flex items-center justify-between mb-1.5">
            <p className="text-[11px] font-bold text-red-600">
                ⚠ {gaveUp.length} item{gaveUp.length === 1 ? "" : "s"} gave up after 3 failed attempts -{" "}
                {onSkip
                    ? "usually because the product was deleted from Walmart. Skip to finish restoring everything else."
                    : "check these on Walmart manually."}
            </p>
            <button
                onClick={() => downloadCsv(gaveUp.map((f) => ({ sku: f.sku, sf_stock: `wanted ${f.attempted_value}, error: ${f.error}` })))}
                className="text-[10px] text-teal-600 hover:text-teal-700 font-semibold underline flex-shrink-0"
            >
                Download list
            </button>
        </div>
        <div className="max-h-32 overflow-y-auto border border-red-100 rounded-lg bg-red-50 p-2 space-y-1">
            {gaveUp.map((f, i) => (
                <div key={i} className="text-[10px] text-red-700 font-mono">
                    <span className="font-bold">{f.sku}</span> → wanted {f.attempted_value}, got: {f.error}
                </div>
            ))}
        </div>
        {onSkip && (
            <button
                onClick={onSkip}
                className="mt-2 px-4 py-2 bg-white border-2 border-red-200 text-red-600 rounded-xl font-bold text-xs hover:bg-red-50 transition"
            >
                Skip {gaveUp.length === 1 ? "this item" : `these ${gaveUp.length} items`} & finish
            </button>
        )}
    </div>
);

const downloadCsv = (items) => {
    const headers = ["SKU", "SF Stock (before vacation mode)"];
    const rows = items.map((it) => [it.sku, it.sf_stock]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vacation_mode_backup_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
};

const VacationModeManager = () => {
    const [loadingStatus, setLoadingStatus] = useState(true);
    const [status, setStatus] = useState(null); // null | 'prepared' | 'active' | 'restoring'
    const [totalItems, setTotalItems] = useState(0);
    const [processedCount, setProcessedCount] = useState(0);
    const [backupItems, setBackupItems] = useState([]);

    const [stage, setStage] = useState("idle"); // idle | explain | preparing | confirmZero | activating | restoring
    const [pendingItems, setPendingItems] = useState([]);
    const [error, setError] = useState("");
    const [progress, setProgress] = useState({ done: 0, total: 0, failed: [] });
    const [gaveUp, setGaveUp] = useState([]); // permanently-failed SKUs (3 retries exhausted) — surfaced until resolved

    const refreshStatus = useCallback(async () => {
        setLoadingStatus(true);
        try {
            const d = await getVacationModeStatus();
            setStatus(d.active ? d.status : null);
            setTotalItems(d.total_items || 0);
            setProcessedCount(d.processed_count || 0);
            setGaveUp(d.gave_up || []);
            setBackupItems(d.items || []);
        } catch (e) {
            setError(e.message || "Failed to load Vacation Mode status");
        } finally {
            setLoadingStatus(false);
        }
    }, []);

    useEffect(() => { refreshStatus(); }, [refreshStatus]);

    const handlePrepare = async () => {
        setError("");
        setStage("preparing");
        try {
            const d = await prepareVacationMode();
            setPendingItems(d.items || []);
            downloadCsv(d.items || []);
            setStage("confirmZero");
        } catch (e) {
            setError(e.message || "Failed to prepare Vacation Mode");
            setStage("idle");
        }
    };

    const handleCancelPrepared = async () => {
        setError("");
        try {
            await cancelVacationMode();
            setStage("idle");
            setPendingItems([]);
            refreshStatus();
        } catch (e) {
            setError(e.message || "Failed to cancel");
        }
    };

    const runChunkLoop = async (pageFn, onDone) => {
        setError("");
        let failedAll = [];
        try {
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const res = await pageFn();
                failedAll = [...failedAll, ...(res.failed_this_page || [])];
                setProgress({ done: res.total_processed, total: res.total_items, failed: failedAll });
                setGaveUp(res.gave_up || []);
                if (res.done) break;
            }
            onDone();
        } catch (e) {
            setError(e.message || "Vacation Mode process failed mid-way — safe to retry, already-processed items are skipped automatically.");
            setStage("idle");
        }
    };

    const handleActivate = () => {
        setStage("activating");
        runChunkLoop(() => activateVacationModePage(25), () => { setStage("idle"); refreshStatus(); });
    };

    const handleSkipFailed = async () => {
        const ok = window.confirm(
            `Skip ${gaveUp.length} item(s) that couldn't be restored and finish?\n\n` +
            `All other items are already restored. Click "Download list" first if you want to keep a record.`
        );
        if (!ok) return;
        setError("");
        try {
            await skipFailedVacationMode();
            setGaveUp([]);
            refreshStatus();
        } catch (e) {
            setError(e.message || "Failed to skip");
        }
    };

    const handleRestore = () => {
        setStage("restoring");
        runChunkLoop(() => restoreVacationModePage(25), () => { setStage("idle"); refreshStatus(); });
    };

    if (loadingStatus) {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-6">
                <p className="text-sm text-gray-400">Loading Vacation Mode status...</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">🏖️</span>
                <h2 className="text-lg font-bold text-gray-800">Vacation Mode</h2>
            </div>
            <p className="text-xs text-gray-500 mb-4">
                Walmart has no built-in way to pause Seller Fulfilled orders. Vacation Mode works around this by
                temporarily setting every SF item's stock to 0, then restoring it exactly when you turn it back off.
            </p>

            {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-xs">{error}</div>
            )}

            {stage === "idle" && status !== null && backupItems.length > 0 && (
                <button
                    onClick={() => downloadCsv(backupItems)}
                    className="mb-3 flex items-center gap-1.5 text-xs text-teal-600 hover:text-teal-700 font-semibold underline"
                >
                    <Download size={14} /> Re-download backup CSV ({backupItems.length} items)
                </button>
            )}

            {stage === "idle" && status === null && (
                <button onClick={() => setStage("explain")}
                    className="px-5 py-2.5 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold text-sm hover:opacity-90 transition">
                    Start Vacation Mode
                </button>
            )}

            {stage === "idle" && status === "active" && (
                <div>
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-3 text-xs text-orange-700">
                        Vacation Mode is <span className="font-bold">ACTIVE</span> — {totalItems} SF item{totalItems === 1 ? "" : "s"} currently set to 0 stock.
                    </div>
                    {gaveUp.length > 0 && (
                        <GaveUpList gaveUp={gaveUp} />
                    )}
                    <button onClick={handleRestore}
                        className="px-5 py-2.5 bg-gray-800 text-white rounded-xl font-bold text-sm hover:bg-gray-700 transition">
                        Turn Off Vacation Mode (Restore Stock)
                    </button>
                </div>
            )}

            {stage === "idle" && status === "prepared" && (
                <div>
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-3 text-xs text-blue-700">
                        A Vacation Mode snapshot exists but zeroing wasn't finished ({processedCount}/{totalItems} done) —
                        resume it, or restore everything back to its saved values.
                    </div>
                    <div className="flex gap-2">
                        <button onClick={handleActivate}
                            className="px-4 py-2.5 bg-teal-600 text-white rounded-xl font-bold text-sm hover:bg-teal-700 transition">
                            Resume Zeroing Out
                        </button>
                        <button onClick={handleRestore}
                            className="px-4 py-2.5 bg-white border-2 border-gray-200 text-gray-600 rounded-xl font-bold text-sm hover:bg-gray-50 transition">
                            Restore Instead
                        </button>
                    </div>
                </div>
            )}

            {stage === "idle" && status === "restoring" && (
                <div>
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-3 text-xs text-blue-700">
                        Restoring stock was interrupted ({processedCount}/{totalItems} done) — resume to finish.
                    </div>
                    {gaveUp.length > 0 && (
                        <GaveUpList gaveUp={gaveUp} onSkip={handleSkipFailed} />
                    )}
                    {processedCount + gaveUp.length < totalItems && (
                        <button onClick={handleRestore}
                            className="px-5 py-2.5 bg-teal-600 text-white rounded-xl font-bold text-sm hover:bg-teal-700 transition">
                            Resume Restoring
                        </button>
                    )}
                </div>
            )}

            {stage === "explain" && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
                        <div className="flex items-start gap-3 mb-4">
                            <AlertTriangle size={22} className="text-orange-500 flex-shrink-0" />
                            <h3 className="font-bold text-gray-800">What Vacation Mode does</h3>
                        </div>
                        <ul className="text-xs text-gray-600 space-y-2 mb-5 list-disc pl-4">
                            <li>Finds every Seller Fulfilled item with stock above 0.</li>
                            <li>Downloads a local CSV backup of each SKU's current SF stock, and saves the same backup on our server.</li>
                            <li>Once you confirm, sets every one of those items' SF stock to 0 on Walmart, so new SF orders can't come in.</li>
                            <li>WFS stock is untouched — this only affects Seller Fulfilled inventory.</li>
                            <li>When you're ready, turning Vacation Mode off restores every item to its exact saved stock level.</li>
                        </ul>
                        <div className="flex gap-2">
                            <button onClick={() => setStage("idle")}
                                className="flex-1 py-2.5 border border-gray-200 text-gray-500 rounded-xl text-sm font-bold hover:bg-gray-50 transition">
                                Cancel
                            </button>
                            <button onClick={handlePrepare}
                                className="flex-1 py-2.5 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl text-sm font-bold hover:opacity-90 transition">
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {stage === "preparing" && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
                        <Loader size={22} className="animate-spin text-teal-600 mx-auto mb-3" />
                        <p className="text-sm text-gray-600">Finding SF items and saving your backup...</p>
                    </div>
                </div>
            )}

            {stage === "confirmZero" && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
                        <div className="flex items-center gap-2 mb-3">
                            <Download size={18} className="text-green-600" />
                            <h3 className="font-bold text-gray-800">Backup saved</h3>
                        </div>
                        <p className="text-xs text-gray-600 mb-4">
                            A CSV backup downloaded to your computer, and the same {pendingItems.length} SKU/stock pairs are
                            saved on our server. Ready to set all {pendingItems.length} item{pendingItems.length === 1 ? "" : "s"} to 0 stock on Walmart?
                        </p>
                        <div className="flex gap-2">
                            <button onClick={handleCancelPrepared}
                                className="flex-1 py-2.5 border border-gray-200 text-gray-500 rounded-xl text-sm font-bold hover:bg-gray-50 transition">
                                Cancel (undo backup)
                            </button>
                            <button onClick={handleActivate}
                                className="flex-1 py-2.5 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 transition">
                                Yes, Set Stock to 0
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {(stage === "activating" || stage === "restoring") && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
                        <div className="text-center">
                            <Loader size={22} className="animate-spin text-teal-600 mx-auto mb-3" />
                            <p className="text-sm text-gray-700 font-semibold mb-1">
                                {stage === "activating" ? "Setting stock to 0..." : "Restoring stock..."}
                            </p>
                            <p className="text-xs text-gray-400 mb-2">
                                {progress.done} / {progress.total || pendingItems.length || totalItems} processed
                            </p>
                            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden mb-2">
                                <div className="h-full bg-teal-500 transition-all"
                                    style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%` }} />
                            </div>
                        </div>

                        {gaveUp.length > 0 && <div className="mt-3"><GaveUpList gaveUp={gaveUp} /></div>}

                        {progress.failed.length > 0 && (
                            <div className="mt-3 text-left">
                                <div className="flex items-center justify-between mb-1.5">
                                    <p className="text-[11px] font-bold text-red-600">
                                        {progress.failed.length} item{progress.failed.length === 1 ? "" : "s"} failed — safe to retry once this run finishes.
                                    </p>
                                    <button
                                        onClick={() => downloadCsv(progress.failed.map((f) => ({ sku: f.sku, sf_stock: `attempted ${f.attempted_value}, error: ${f.error}` })))}
                                        className="text-[10px] text-teal-600 hover:text-teal-700 font-semibold underline flex-shrink-0"
                                    >
                                        Download failures CSV
                                    </button>
                                </div>
                                <div className="max-h-32 overflow-y-auto border border-red-100 rounded-lg bg-red-50 p-2 space-y-1">
                                    {progress.failed.map((f, i) => (
                                        <div key={i} className="text-[10px] text-red-700 font-mono">
                                            <span className="font-bold">{f.sku}</span> → tried {f.attempted_value}: {f.error}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <p className="text-[10px] text-gray-400 mt-3 text-center">This respects Walmart's rate limits — please don't close this tab.</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VacationModeManager;